import type { FeatureEvents, OperationalEvent, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, OperationalAction, OperationalSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

import { normalizeConnectorType } from './normalize-connector-type';

type ScreenKeys =
  | 'thirdPartyConnectModalScreen'
  | 'thirdPartyManageModalScreen'
  | 'thirdPartyDisconnectModalScreen'
  | 'rovoSelectProductStepScreen'
  | 'rovoConnectSuccessScreen'
  | 'rovoConnectSlackScreen'
  | 'rovoAddDataSourceScreen'
  | 'rovoConnectCustomWebsiteScreen'
  | 'thirdPartyLimitByDateTabScreen';

type UIKeys =
  | 'addSlackConnector'
  | 'select3pDataSourceProduct'
  | 'addRovoDataSource'
  | 'removeSlackConnector'
  | 'connectOrManageApp'
  | 'disconnectConnector'
  | 'cancelDisconnect'
  | 'nextRovoSelectProduct'
  | 'selectAuthorizationConnect'
  | 'backRovoSelectProduct'
  | 'closeRovoSelectProduct'
  | 'disableLiteConnector'
  | 'enableLiteConnector'
  | 'openStartDateSelection'
  | 'selectStartDate'
  | 'saveChooseContentModal'
  | 'connectorFormFirstInteraction';

export const thirdPartyConnectModalScreen = 'thirdPartyConnectModalScreen';
export const thirdPartyManageModalScreen = 'thirdPartyManageModalScreen';
export const thirdPartyConnectScreen = 'thirdPartyConnectScreen';
export const thirdPartyDisconnectModalScreen = 'thirdPartyDisconnectModalScreen';
export const rovoSelectProductStepScreen = 'rovoSelectProductStepScreen';
export const rovoConnectSuccessScreen = 'rovoConnectSuccessScreen';
export const rovoConnectSlackScreen = 'rovoConnectSlackScreen';
export const rovoConnectCustomWebsiteScreen = 'rovoConnectCustomWebsiteScreen';
export const rovoAddDataSourceScreen = 'rovoAddDataSourceScreen';
export const thirdPartyDisableLiteConnectorModalScreen = 'thirdPartyDisableLiteConnectorModalScreen';
export const thirdPartyEnableLiteConnectorModalScreen = 'thirdPartyEnableLiteConnectorModalScreen';
export const thirdPartyLimitByDateTabScreen = 'thirdPartyLimitByDateTabScreen';

export const thirdPartyConnectorEvents: FeatureEvents<ScreenKeys, UIKeys, string, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/ETF/pages/3696480870/Rovo+Admin+Hub+Beta+-+Product+Analytics+Spec',

  screen: {
    thirdPartyConnectModalScreen: (attributes: { source: string; connectorType: string }): ScreenEvent => ({
      data: {
        name: thirdPartyConnectModalScreen,
        attributes: {
          integration: attributes.source,
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/67294',
    }),
    thirdPartyManageModalScreen: (attributes: { source: string; connectorType: string }): ScreenEvent => ({
      data: {
        name: thirdPartyManageModalScreen,
        attributes: {
          integration: attributes.source,
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/67295',
    }),
    thirdPartyDisconnectModalScreen: (): ScreenEvent => ({
      data: {
        name: thirdPartyDisconnectModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72359',
    }),
    rovoSelectProductStepScreen: (attributes: { cloudId: string; source: string }): ScreenEvent => ({
      cloudId: attributes.cloudId,
      data: {
        name: rovoSelectProductStepScreen,
        attributes: {
          source: attributes.source,
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72360',
    }),
    rovoConnectSuccessScreen: (attributes: { source: string; cloudId: string }): ScreenEvent => ({
      cloudId: attributes.cloudId,
      data: {
        name: rovoConnectSuccessScreen,
        attributes: {
          source: attributes.source,
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72361',
    }),
    rovoConnectSlackScreen: (): ScreenEvent => ({
      data: {
        name: rovoConnectSlackScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72362',
    }),

    rovoConnectCustomWebsiteScreen: (): ScreenEvent => ({
      data: {
        name: rovoConnectCustomWebsiteScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72365',
    }),
    rovoAddDataSourceScreen: (attributes: { product: string; source: string; cloudId: string }): ScreenEvent => ({
      cloudId: attributes.cloudId,
      data: {
        name: rovoAddDataSourceScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
          source: attributes.source,
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72584',
    }),

    thirdPartyLimitByDateTabScreen: (attributes: { connectorType: string }): ScreenEvent => ({
      data: {
        name: thirdPartyLimitByDateTabScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91304',
    }),
  },

  ui: {
    connectOrManageApp: (attributes: { source: string; connectorType: string; isConnected: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'connectOrManageApp',
        source: thirdPartyConnectScreen,
        attributes: {
          integration: attributes.source,
          connectorType: normalizeConnectorType(attributes.connectorType),
          isConnected: attributes.isConnected,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/67260',
    }),

    addSlackConnector: (attributes: { source: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addSlackConnector',
        source: thirdPartyConnectModalScreen,
        attributes: {
          integration: attributes.source,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/67278',
    }),
    addRovoDataSource: (attributes: { connectorType?: string; providerId?: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'addRovoDataSource',
        source: thirdPartyConnectModalScreen,
        attributes: {
          ...attributes,
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72585',
    }),
    select3pDataSourceProduct: (attributes: { product: string; cloudId: string }): UIEvent => ({
      cloudId: attributes.cloudId,
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'select3pDataSourceProduct',
        source: thirdPartyConnectModalScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/84261',
    }),
    removeSlackConnector: (attributes: { source: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'removeSlackConnector',
        source: thirdPartyManageModalScreen,
        attributes: {
          integration: attributes.source,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/67281',
    }),
    disconnectConnector: (attributes: { product: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'disconnectConnector',
        source: thirdPartyDisconnectModalScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72367',
    }),
    cancelDisconnect: (attributes: { product: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'cancelDisconnect',
        source: thirdPartyDisconnectModalScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72368',
    }),
    nextRovoSelectProduct: (attributes: { product: string; cloudId: string }): UIEvent => ({
      cloudId: attributes.cloudId,
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'nextRovoSelectProduct',
        source: rovoSelectProductStepScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72369',
    }),
    selectAuthorizationConnect: (attributes: { connectorType: string; cloudId: string; providerId?: string }): UIEvent => ({
      cloudId: attributes.cloudId,
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'selectAuthorizationConnect',
        source: thirdPartyConnectModalScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
          providerId: attributes.providerId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91146',
    }),
    backRovoSelectProduct: (attributes: { connectorType: string; cloudId: string }): UIEvent => ({
      cloudId: attributes.cloudId,
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'backRovoSelectProduct',
        source: rovoSelectProductStepScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72370',
    }),
    closeRovoSelectProduct: (attributes: { connectorType: string; cloudId: string }): UIEvent => ({
      cloudId: attributes.cloudId,
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'closeRovoSelectProduct',
        source: rovoSelectProductStepScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72371',
    }),
    disableLiteConnector: (attributes: { product: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'disableLiteConnector',
        source: thirdPartyDisableLiteConnectorModalScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91014',
    }),
    enableLiteConnector: (attributes: { product: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'enableLiteConnector',
        source: thirdPartyEnableLiteConnectorModalScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91015',
    }),

    openStartDateSelection: (attributes: { connectorType?: string; providerId?: string }): UIEvent => ({
      data: {
        action: Action.Opened,
        actionSubject: ActionSubject.Datepicker,
        actionSubjectId: 'openStartDateSelection',
        source: thirdPartyConnectModalScreen,
        attributes: {
          ...attributes,
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91535',
    }),

    selectStartDate: (attributes: { connectorType?: string; dateSelected: string; dateSent: string; providerId?: string }): UIEvent => ({
      data: {
        action: Action.Selected,
        actionSubject: ActionSubject.Datepicker,
        actionSubjectId: 'selectStartDate',
        source: thirdPartyConnectModalScreen,
        attributes: {
          ...attributes,
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91534',
    }),

    saveChooseContentModal: (attributes: { connectorType?: string; providerId?: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'saveChooseContentModal',
        source: thirdPartyConnectModalScreen,
        attributes: {
          ...attributes,
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/91311',
    }),

    connectorFormFirstInteraction: (attributes: { connectorType?: string; providerId?: string }): UIEvent => ({
      data: {
        action: Action.Changed,
        actionSubject: ActionSubject.Form,
        actionSubjectId: 'connectorForm',
        source: thirdPartyConnectModalScreen,
        attributes: {
          ...attributes,
          connectorType: normalizeConnectorType(attributes.connectorType),
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/97799',
    }),
  },

  track: {
    connectRovoSearchDataSourceSuccess: (attributes: {
      source: string;
      connectorType: string;
      cloudId: string;
      [key: string]: any;
    }): TrackEvent => {
      const { source, connectorType, cloudId, ...additionalAttributes } = attributes;

      return {
        data: {
          action: TrackAction.Created,
          actionSubject: TrackActionSubject.RovoSearchConnection,
          source,
          attributes: {
            connectorType: normalizeConnectorType(connectorType),
            cloudId,
            ...additionalAttributes,
          },
        },
        dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69526',
      };
    },
    connectRovoSearchDataSourceFailure: (attributes: { source: string; connectorType: string; cloudId: string }): TrackEvent => ({
      data: {
        action: TrackAction.CreationFailed,
        actionSubject: TrackActionSubject.RovoSearchConnection,
        source: attributes.source,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69527',
    }),
    deleteRovoSearchDataSourceSuccess: (attributes: { source: string; connectorType: string; cloudId: string }): TrackEvent => ({
      data: {
        action: TrackAction.Deleted,
        actionSubject: TrackActionSubject.RovoSearchConnection,
        source: attributes.source,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69900',
    }),
    deleteRovoSearchDataSourceFailure: (attributes: { source: string; connectorType: string; cloudId: string }): TrackEvent => ({
      data: {
        action: TrackAction.CanceledDeletion,
        actionSubject: TrackActionSubject.RovoSearchConnection,
        source: attributes.source,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/69901',
    }),
    disconnectRovo3PConnector: (attributes: { product: string; isSuccess: boolean }): TrackEvent => ({
      data: {
        action: TrackAction.Deleted,
        actionSubject: TrackActionSubject.RovoConnector,
        actionSubjectId: 'disconnectRovo3PConnector',
        source: thirdPartyDisconnectModalScreen,
        attributes: {
          product: normalizeConnectorType(attributes.product),
          isSuccess: attributes.isSuccess,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/72372',
    }),
  },

  operational: {
    oauthAuthorizationSuccess: (attributes: {
      connectorType: string;
      isEditing: boolean;
      cloudId: string;
      providerId?: string;
    }): OperationalEvent => ({
      cloudId: attributes.cloudId,
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: OperationalSubject.AuthorizationOutcome,
        source: attributes.isEditing ? thirdPartyManageModalScreen : thirdPartyConnectModalScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
          providerId: attributes.providerId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90457',
    }),

    oauthAuthorizationError: (attributes: {
      connectorType: string;
      isEditing: boolean;
      errorMessage?: string;
      errorType?: string;
      cloudId: string;
      providerId?: string;
    }): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: OperationalSubject.AuthorizationOutcome,
        source: attributes.isEditing ? thirdPartyManageModalScreen : thirdPartyConnectModalScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          cloudId: attributes.cloudId,
          errorMessage: attributes.errorMessage,
          errorType: attributes.errorType,
          providerId: attributes.providerId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90456',
    }),
    connectorCreationFormSubmitSuccess: (attributes: {
      connectorType: string;
      providerId?: string;
      cloudId?: string;
    }): OperationalEvent => ({
      data: {
        action: OperationalAction.Succeeded,
        actionSubject: OperationalSubject.OnConnectorCreationFormSubmitOutcome,
        source: thirdPartyConnectModalScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          providerId: attributes.providerId,
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90462',
    }),
    connectorCreationFormSubmitError: (attributes: {
      connectorType: string;
      errorCode?: string;
      errorMessage?: string;
      errorType?: string;
      isEditing?: boolean;
      providerId?: string;
      cloudId?: string;
    }): OperationalEvent => ({
      data: {
        action: OperationalAction.Failed,
        actionSubject: OperationalSubject.OnConnectorCreationFormSubmitOutcome,
        source: thirdPartyConnectModalScreen,
        attributes: {
          connectorType: normalizeConnectorType(attributes.connectorType),
          errorCode: attributes.errorCode,
          errorMessage: attributes.errorMessage,
          errorType: attributes.errorType,
          isEditing: attributes.isEditing,
          providerId: attributes.providerId,
          cloudId: attributes.cloudId,
        },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/90463',
    }),
  },
};
